/**
 * Core type definitions for the shopping cart system
 */

import type { Product as EcomProduct } from '@/components/ecom-policy/types'
import type { GreenpoolAsset } from '@/components/ecom-policy/types'

export type ProductId = string | number
export type PolicyId = string | number

/**
 * Minimal cart item data persisted to localStorage
 * Now includes policyId to track which policy this product came from
 * For Greenpool products, productId will store the assetId and isGreenpoolProduct flag will be true
 */
export type CartItemPersisted = {
  policyId: PolicyId
  productId: ProductId
  qty: number
  isGreenpoolProduct: boolean
}

/**
 * Policy information
 */
export type Policy = {
  id: PolicyId
  name: string
}

export type BaseProduct = EcomProduct

/**
 * Full product details from the API
 */
export type Product = BaseProduct & {
  id: ProductId
  qty?: number
}

type BaseAsset = GreenpoolAsset
/**
 * Full asset details from the API
 */
export type Asset = BaseAsset & {
  id: ProductId
}

/**
 * Cart item with optional product details (undefined until hydration)
 * Unique by combination of policyId + productId
 */
export type CartItem = CartItemPersisted & {
  product?: Product
  asset?: Asset
  policy?: Policy
}

/**
 * Composite key for cart items (policyId + productId)
 */
export type CartItemKey = string // Format: "policyId:productId"

/**
 * Policy terms acceptance status
 * grossSalaryTaxBracket is optional and may be undefined, 'Top' or 'Base'
 */
export type PolicyTermsStatus = {
  isTermsAccepted: boolean
  grossSalaryTaxBracket?: 'Top' | 'Base'
}

/**
 * Main cart state structure
 * Items are now keyed by "policyId:productId" to handle same product from different policies
 */
export type CartState = {
  items: Record<CartItemKey, CartItem>
  version: number
  isTermsAccepted: boolean
  selectedAddressId: number | undefined
  customAddress?: {
    address: string
    zip: string
    city: string
  }
  isPrivateAddress?: boolean
  policiesTermsStatus: Record<PolicyId, PolicyTermsStatus>
  /** Logged-in user that this cart belongs to */
  userId: number | null
  lastUpdated: number | null | Date
}

/**
 * Persisted cart data structure
 */
export type PersistedCartData = {
  version: number
  items: CartItemPersisted[]
  userId: number | null
  lastUpdated: Date | number | null
}

/**
 * Cart reducer action types
 */
export type CartAction =
  | { type: 'ADD_ITEM'; payload: { policyId: PolicyId; product: Product; qty?: number } }
  | { type: 'ADD_ITEMS_PER_POLICY'; payload: { policyId: PolicyId; products: Product[] } }
  | { type: 'REMOVE_ITEM'; payload: { policyId: PolicyId; productId: ProductId } }
  | {
      type: 'ADD_GREENPOOL_ITEM'
      payload: { policyId: PolicyId; asset: Asset }
    }
  | { type: 'REMOVE_GREENPOOL_ITEM'; payload: { policyId: PolicyId; assetId: ProductId } }
  | { type: 'INCREMENT_QTY'; payload: { policyId: PolicyId; productId: ProductId; delta?: number } }
  | { type: 'DECREMENT_QTY'; payload: { policyId: PolicyId; productId: ProductId; delta?: number } }
  | { type: 'SET_QTY'; payload: { policyId: PolicyId; productId: ProductId; qty: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'CLEAR_POLICY'; payload: { policyId: PolicyId } }
  | { type: 'HYDRATE_PRODUCTS'; payload: { productsById: Record<ProductId, Product> } }
  | { type: 'HYDRATE_POLICIES'; payload: { policiesById: Record<PolicyId, Policy> } }
  | { type: 'HYDRATE_ITEMS'; payload: { items: CartItem[] } }
  | { type: 'SYNC_FROM_STORAGE'; payload: { items: CartItemPersisted[] } }
  | { type: 'SET_TERMS_ACCEPTED'; payload: { accepted: boolean } }
  | { type: 'SET_SELECTED_ADDRESS'; payload: { addressId: number | undefined } }
  | { type: 'CLEAR_CONFIRM_DATA' }
  | { type: 'SET_CUSTOM_ADDRESS'; payload: { address: string; zip: string; city: string } }
  | { type: 'SET_IS_PRIVATE_ADDRESS'; payload: { isPrivateAddress?: boolean } }
  | { type: 'RESET_CART_FOR_USER'; payload: { userId: number | null } }
  | { type: 'SET_USER_ID'; payload: { userId: number | null } }
  | { type: 'ADD_POLICY_TERMS_STATUS'; payload: { policyId: PolicyId; status: PolicyTermsStatus } }
/**
 * Helper to create cart item key
 */
export const createCartItemKey = (policyId: PolicyId, productId: ProductId): CartItemKey =>
  `${policyId}:${productId}`

/**
 * Helper to parse cart item key
 */
export const parseCartItemKey = (
  key: CartItemKey
): { policyId: PolicyId; productId: ProductId } => {
  const [policyId, productId] = key.split(':')
  return { policyId, productId }
}

/**
 * Cart context actions interface
 */
export type CartActions = {
  addItem: (policyId: PolicyId, product: Product, qty?: number) => void
  addItemsPerPolicy: (policyId: PolicyId, products: Product[]) => void
  removeItem: (policyId: PolicyId, productId: ProductId) => void
  addGreenpoolItem: (policyId: PolicyId, asset: Asset) => void
  removeGreenpoolItem: (policyId: PolicyId, assetId: ProductId) => void
  incrementQty: (policyId: PolicyId, productId: ProductId, delta?: number) => void
  decrementQty: (policyId: PolicyId, productId: ProductId, delta?: number) => void
  setQty: (policyId: PolicyId, productId: ProductId, qty: number) => void
  clearCart: () => void
  clearPolicy: (policyId: PolicyId) => void
  setIsTermsAccepted: (accepted: boolean) => void
  setSelectedAddress: (addressId: number | undefined) => void
  clearConfirmData: () => void
  setCustomAddress: (address: string, zip: string, city: string) => void
  setIsPrivateAddress: (isPrivateAddress?: boolean) => void
  addPolicyTermsStatus: (policyId: PolicyId, status: PolicyTermsStatus) => void
}

/**
 * Cart utilities interface
 */
export type CartUtils = {
  getLineSubtotal: (policyId: PolicyId, productId: ProductId) => number
  hasProduct: (policyId: PolicyId, productId: ProductId) => boolean
  qtyOf: (policyId: PolicyId, productId: ProductId) => number
  getItemsByPolicy: (policyId: PolicyId) => CartItem[]
  getProductsByPolicy: (policyId: PolicyId) => CartItem[]
  getAccessoriesByPolicy: (policyId: PolicyId) => CartItem[]
  getItemsByPolicyInOrder: (policyId: PolicyId) => CartItem[]
  getItemsGroupedByPolicy: () => Record<PolicyId, CartItem[]>
  getNonMandatoryItemsGroupedByPolicy: () => Record<PolicyId, CartItem[]>
  getPolicyIds: () => PolicyId[]
  getPolicyIdsWithoutGreenpool: () => PolicyId[]
  getItemInCart: (productId: ProductId) => CartItem | undefined
  hasNonGreenpoolItem: (policyId: PolicyId) => boolean
  hasGreenpoolItem: (policyId: PolicyId) => boolean
  getGreenpoolItem: (policyId: PolicyId) => CartItem | undefined
  getGreenpoolItemsGroupedByPolicy: () => Record<PolicyId, CartItem[]>
  getPolicyTermsStatus: (policyId: PolicyId) => PolicyTermsStatus | undefined
}

/**
 * Complete cart context value
 */
export type CartContextValue = {
  items: CartItem[]
  itemsMap: Record<CartItemKey, CartItem>
  greenpoolItems: CartItem[]
  totalQty: number
  subtotal: number
  isHydrating: boolean
  isTermsAccepted: boolean
  selectedAddressId: number | undefined
  customAddress?: { address: string; zip: string; city: string }
  isPrivateAddress?: boolean
  policiesTermsStatus: Record<PolicyId, PolicyTermsStatus>
  userId: number | null
} & CartActions &
  CartUtils
